"use client";

import React from "react";
import TrackedLink from "@components/TrackedLink";
import ScheduleCall from "@components/ScheduleCall";
import { track } from "@lib/track";

export default function DiscoveryCallClient() {
  const onPlay = (label: string) => {
    try {
      track(
        "video_play",
        { label, component: "DiscoveryCallPage.Video" },
        { event: "video_play", label, component: "DiscoveryCallPage.Video" }
      );
    } catch {}
  };

  const scrollToScheduler: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    document.getElementById("schedule-call")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onFAQToggle = (question: string, isOpen: boolean) => {
    try {
      track(
        "accordion_toggle",
        { label: `${isOpen ? "Opened" : "Closed"}: ${question}`, component: "DiscoveryCallPage.FAQ" },
        { event: "accordion_toggle", label: `${isOpen ? "Opened" : "Closed"}: ${question}`, component: "DiscoveryCallPage.FAQ" }
      );
    } catch {}
  };

  return (
    <div className="relative bg-[var(--color-brand)] text-[var(--text-on-dark)]">
      {/* Hero Section */}
      <header className="mx-auto max-w-5xl px-5 md:px-10 py-10 md:py-16 text-center">
        <h1 className="text-3xl md:text-5xl font-extrabold leading-tight tracking-tight">
          Take the First Step <br className="hidden sm:block" /> Toward Feeling Your Best
        </h1>
        <p className="mt-3 md:mt-5 text-sm md:text-lg opacity-90 max-w-2xl mx-auto">
          Book a <strong>free 15-minute Discovery Call</strong> with a Naturopathic Nutritionist —
          a calm, no-pressure chat to understand your goals and map your next step toward balance and energy.
        </p>
      </header>

      {/* Main Body */}
      <main className="mx-auto max-w-5xl -mt-2 md:-mt-6 bg-[var(--bg-surface)] rounded-t-3xl px-5 md:px-10 pb-20 shadow-2xl text-[var(--text-on-light)]">
        {/* Video Section */}
        <section id="video" className="pt-10 md:pt-14 text-center">
          <h2 className="text-lg md:text-2xl font-bold text-[var(--text-brand)]">
            What to Expect in 15 Minutes
          </h2>
          <p className="mt-2 text-[var(--text-on-light-muted)] max-w-md mx-auto text-sm md:text-base">
            You'll connect one-on-one with Niki for a warm, open conversation that helps you find clarity and direction.
          </p>

          <div className="mt-6 grid md:grid-cols-3 gap-4 text-[var(--text-on-light)] text-left">
            <div className="rounded-2xl border border-[var(--border-accent)] p-5 shadow-sm">
              <p className="font-semibold text-[var(--text-brand)]">Share your goals</p>
              <p className="text-sm mt-1 text-[var(--text-on-light-muted)]">Energy, digestion, hormones, sleep, weight, or performance.</p>
            </div>
            <div className="rounded-2xl border border-[var(--border-accent)] p-5 shadow-sm">
              <p className="font-semibold text-[var(--text-brand)]">Get a clear next step</p>
              <p className="text-sm mt-1 text-[var(--text-on-light-muted)]">We'll help you understand which path or program suits you best.</p>
            </div>
            <div className="rounded-2xl border border-[var(--border-accent)] p-5 shadow-sm">
              <p className="font-semibold text-[var(--text-brand)]">No pressure, ever</p>
              <p className="text-sm mt-1 text-[var(--text-on-light-muted)]">You'll leave with clarity — commit only if it feels right for you.</p>
            </div>
          </div>

          <div className="mt-8">
            <video
              controls
              preload="none"
              poster="/images/Niki_Charnock.webp"
              className="mx-auto w-full md:w-2/4 max-w-2xl rounded-2xl shadow-lg"
              onPlay={() => onPlay("Discovery Call Explainer")}
            >
              <source src="/images/discoveryCall.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <p className="mt-3 text-sm text-[var(--text-on-light-muted)]">
              Give me 30 seconds to explain what you receive with your free Discovery Call.
            </p>
          </div>
        </section>

        {/* Schedule Section */}
        <section id="schedule-call" className="mt-14 md:mt-20 scroll-mt-24">
          <ScheduleCall
            duration={15}
            meetingType="discovery"
            title="Wellness Discovery Call"
            subtitle="Choose a time that works best for you. No charge. No obligation. Just clarity."
            isFree
            componentName="DiscoveryCallPage"
          />
        </section>

        {/* FAQs Section */}
        <section className="mt-16 md:mt-24">
          <h2 className="text-lg md:text-2xl font-bold text-[var(--text-brand)] text-center mb-6">
            Quick FAQs
          </h2>
          <div className="space-y-4 max-w-2xl mx-auto text-[var(--text-on-light)]">
            {[
              { question: "Is the call really free?", answer: "Yes — no cost, no obligation. It's simply a first conversation." },
              { question: "Do I need to prepare anything?", answer: "Just bring your main goals or concerns — optional notes are great." },
              { question: "What happens after the call?", answer: "If it's a fit, we'll recommend the best next step (consultation, package, or retreat). No pressure — just clarity." },
            ].map((faq, index) => (
              <details
                key={index}
                className="rounded-xl border border-[var(--border-on-light)] p-4 transition-all hover:shadow-sm"
                onToggle={(e) => onFAQToggle(faq.question, (e.target as HTMLDetailsElement).open)}
              >
                <summary className="font-medium cursor-pointer text-[var(--text-brand)]">
                  {faq.question}
                </summary>
                <p className="mt-2 text-sm text-[var(--text-on-light-muted)]">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <div className="mt-16 hidden md:flex justify-center">
          <TrackedLink
            href="#schedule-call"
            onClick={scrollToScheduler}
            variant="solid"
            size="lg"
            color="neutral"
            event="cta_click"
            label="Discovery Call | Book"
            component="DiscoveryCallPage"
          >
            Book a free discovery call
          </TrackedLink>
        </div>
      </main>

      {/* Sticky Mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t border-[var(--border-on-light)] bg-[var(--bg-surface)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--bg-surface)]/70">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <span className="text-sm font-medium text-[var(--text-on-light)]">Ready to get started?</span>
          <TrackedLink
            href="#schedule-call"
            onClick={scrollToScheduler}
            variant="solid"
            size="md"
            color="base"
            event="cta_click"
            label="Discovery Call | Book"
            component="DiscoveryCallPage"
          >
            Free discovery call
          </TrackedLink>
        </div>
      </div>
    </div>
  );
}
